import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { debug } from '@/utils/debug';
import {
  usePendingChangesStore,
  type AnyEntityStatusAction,
  type DiscardCallback,
  type EntityStatusChange,
  type EntityType,
  type Platform,
} from '@/store/pendingChangesStore';

/**
 * Shared optimistic-update brain for campaign-element status changes.
 *
 * Extracted from the verbatim copies that previously lived in
 * `CampaignElementsList` (Google) and `MetaCampaignElementsList` (Meta).
 * Keeping both surfaces on this one path means any platform-specific
 * bug isolates to the config / data shape / leaf JSX — never the brain.
 *
 * Two layers of "pending":
 *  1. Local `localUpdates: Map<entityId, LocalUpdate>` — instant visual
 *     feedback, no network. Cleared on Discard via the registered
 *     callback or implicitly on component unmount/remount.
 *  2. `pendingChangesStore` queue — persists across navigation until
 *     Save (success) / Discard / logout. Both Google and Meta queue
 *     records carry a `platform` discriminator that partitions the
 *     dispatch at save time (`saveAllChanges`).
 */

export interface LocalEntityUpdate {
  /** Post-action status string in the platform's vocab — 'ENABLED'/'ACTIVE'/'PAUSED'. */
  status?: string;
  /** Whether the entity has been queued for the terminal action (remove/archive). */
  terminal?: boolean;
}

export interface PendingActionState<
  TAction extends AnyEntityStatusAction,
  TType extends EntityType,
> {
  type: TType;
  action: TAction;
  entityId: string;
  entityName: string;
  /** Google ad's `ad_group_id` or Meta ad's `ad_set_id`. Stored as `adGroupId` to match the store schema. */
  adGroupId?: string;
}

export interface OptimisticUpdateConfig<
  TAction extends AnyEntityStatusAction,
  TType extends EntityType,
> {
  campaignId: string;
  platform: Platform;
  /** Valid action set for this platform — Google: `[pause,enable,remove]`, Meta: `[pause,enable,archive]`. */
  validActions: readonly TAction[];
  /** Terminal action (remove/archive). Used by `handleAction` and the kebab/Confirm flow. */
  terminalAction: TAction;
  /** Status string written into `localUpdates` when the user enables — Google: `ENABLED`, Meta: `ACTIVE`. */
  activeStatus: string;
  /** Status string written into `localUpdates` when the user pauses — both `PAUSED`. */
  pausedStatus: string;
  /** Original-status values that should hide the entity from the tree — Google: `[REMOVED]`, Meta: `[ARCHIVED,DELETED]`. */
  hiddenStatuses: readonly string[];
  /** Prefix for the discard-callback registry key. Final key is `${prefix}-${campaignId}`. */
  discardCallbackKeyPrefix: string;
  /** Called after every queued action so the parent can refetch / re-derive. */
  onElementUpdated: () => void;
  /** Called when the terminal action fires so the parent can apply page-wide optimistic removal. */
  onTerminalAction?: (entityId: string) => void;
  /** Formats the entity type for the success toast. Google: `'ad_group'→'ad group'`; Meta: `'ad_set'→'ad set'`. */
  formatToastSubject: (entityType: TType) => string;
}

export interface OptimisticUpdateAPI<
  TAction extends AnyEntityStatusAction,
  TType extends EntityType,
> {
  hasPendingChange: (entityId: string) => boolean;
  getPendingAction: (entityId: string) => TAction | null;
  getEffectiveStatus: (entityId: string, originalStatus: string) => string;
  isHidden: (entityId: string, originalStatus: string) => boolean;
  handleAction: (
    type: TType,
    entityId: string,
    action: TAction,
    entityName: string,
    adGroupId?: string,
  ) => void;
  pendingAction: PendingActionState<TAction, TType> | null;
  setPendingAction: (a: PendingActionState<TAction, TType> | null) => void;
  confirmPendingAction: () => void;
}

export function useOptimisticEntityUpdates<
  TAction extends AnyEntityStatusAction,
  TType extends EntityType,
>(
  config: OptimisticUpdateConfig<TAction, TType>,
): OptimisticUpdateAPI<TAction, TType> {
  const {
    campaignId,
    platform,
    validActions,
    terminalAction,
    activeStatus,
    pausedStatus,
    hiddenStatuses,
    discardCallbackKeyPrefix,
    onElementUpdated,
    onTerminalAction,
    formatToastSubject,
  } = config;

  const [localUpdates, setLocalUpdates] = useState<Map<string, LocalEntityUpdate>>(
    () => new Map(),
  );
  const [pendingAction, setPendingAction] =
    useState<PendingActionState<TAction, TType> | null>(null);

  // Whole-store destructure intentionally — subscribes to all store
  // state slices, which is the only re-render trigger reliable across
  // navigation + minified prod builds. Selector subscription was tried
  // (commits prior to fe264e3) and did not re-render reliably.
  const { queueStatusChange, changes, registerDiscardCallback, unregisterDiscardCallback } =
    usePendingChangesStore();

  // Register discard callback to clear local optimistic state when the
  // user clicks Discard on the SaveChangesBar.
  useEffect(() => {
    const key = `${discardCallbackKeyPrefix}-${campaignId}`;
    const restoreState: DiscardCallback = () => {
      debug.log('useOptimisticEntityUpdates: discard callback fired', { key, platform });
      setLocalUpdates(new Map());
      setPendingAction(null);
    };
    registerDiscardCallback(key, restoreState);
    return () => {
      unregisterDiscardCallback(key);
    };
  }, [
    campaignId,
    platform,
    discardCallbackKeyPrefix,
    registerDiscardCallback,
    unregisterDiscardCallback,
  ]);


  // Per-platform filter — rejects any change from a different platform
  // (collision protection: same numeric IDs can exist on both platforms
  // for small test accounts) AND any action outside this platform's
  // valid set (defense against a buggy queue site that slips, e.g.,
  // `archive` into a Google surface where the consumer would cast to
  // EntityStatusAction).
  const isOwnPlatformChange = useCallback(
    (change: EntityStatusChange): boolean => {
      const platformOk =
        platform === 'google_ads'
          ? change.platform === undefined || change.platform === 'google_ads'
          : change.platform === platform;
      if (!platformOk) return false;
      return (validActions as readonly string[]).includes(change.action);
    },
    [platform, validActions],
  );

  const hasPendingChange = useCallback(
    (entityId: string): boolean => {
      for (const change of changes.statusChanges.values()) {
        if (change.entityId === entityId && isOwnPlatformChange(change)) {
          return true;
        }
      }
      return false;
    },
    [changes.statusChanges, isOwnPlatformChange],
  );

  const getPendingAction = useCallback(
    (entityId: string): TAction | null => {
      for (const change of changes.statusChanges.values()) {
        if (change.entityId === entityId && isOwnPlatformChange(change)) {
          return change.action as TAction;
        }
      }
      return null;
    },
    [changes.statusChanges, isOwnPlatformChange],
  );

  const getEffectiveStatus = useCallback(
    (entityId: string, originalStatus: string): string => {
      const local = localUpdates.get(entityId);
      if (local?.status) return local.status;
      const pending = getPendingAction(entityId);
      if (pending === 'enable') return activeStatus;
      if (pending === 'pause') return pausedStatus;
      return originalStatus;
    },
    [localUpdates, getPendingAction, activeStatus, pausedStatus],
  );

  const isHidden = useCallback(
    (entityId: string, originalStatus: string): boolean => {
      const local = localUpdates.get(entityId);
      if (local?.terminal) return true;
      if (getPendingAction(entityId) === terminalAction) return true;
      return hiddenStatuses.includes((originalStatus ?? '').toUpperCase());
    },
    [localUpdates, getPendingAction, terminalAction, hiddenStatuses],
  );

  const handleAction = useCallback(
    (
      type: TType,
      entityId: string,
      action: TAction,
      entityName: string,
      adGroupId?: string,
    ): void => {
      debug.log('useOptimisticEntityUpdates: handleAction', {
        platform,
        type,
        action,
        entityId,
        campaignId,
        adGroupId,
      });

      setLocalUpdates((prev) => {
        const next = new Map(prev);
        if (action === terminalAction) {
          next.set(entityId, { terminal: true });
        } else if (action === 'enable') {
          next.set(entityId, { status: activeStatus });
        } else if (action === 'pause') {
          next.set(entityId, { status: pausedStatus });
        }
        return next;
      });

      if (action === terminalAction && onTerminalAction) {
        onTerminalAction(entityId);
      }

      queueStatusChange({
        entityType: type,
        entityId,
        campaignId,
        adGroupId,
        action,
        entityName,
        platform,
      });

      const verb =
        action === 'enable'
          ? 'enable'
          : action === 'pause'
            ? 'pause'
            : action === 'remove'
              ? 'remove'
              : action === 'archive'
                ? 'archive'
                : (action as string);
      const subject = formatToastSubject(type);
      toast.success(`Queued: ${verb} ${subject}. Click "Save Changes" to apply.`, {
        duration: 3000,
        icon: '📋',
      });

      setPendingAction(null);
      onElementUpdated();
    },
    [
      platform,
      campaignId,
      terminalAction,
      activeStatus,
      pausedStatus,
      onTerminalAction,
      queueStatusChange,
      formatToastSubject,
      onElementUpdated,
    ],
  );

  const confirmPendingAction = useCallback((): void => {
    if (!pendingAction) return;
    handleAction(
      pendingAction.type,
      pendingAction.entityId,
      pendingAction.action,
      pendingAction.entityName,
      pendingAction.adGroupId,
    );
  }, [pendingAction, handleAction]);

  return {
    hasPendingChange,
    getPendingAction,
    getEffectiveStatus,
    isHidden,
    handleAction,
    pendingAction,
    setPendingAction,
    confirmPendingAction,
  };
}
